import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';

import CD_LOGO from '../img/cd-logo.png';
import useAvailableMasseuses from '../hooks/useAvailableMasseuses';
import { useMutation } from '@tanstack/react-query';
import customFetch, { ApiError } from '../api/http/fetcher';
import {
  getLoungeAccessErrorMessage,
  ApiErrorBody,
} from '../utils/apiErrorMapper';

// Fallback image for Shiatsu masseuses without a profile image
const SHIATSU_FALLBACK_IMAGE =
  'https://clubdynasty.ca/wp-content/uploads/2024/07/Female_Silouhette-square.jpg';

// Lounge Access Screen BG
const LOUNGE_BG: string =
  'https://clubdynasty.ca/wp-content/uploads/2023/05/Lounge-4.jpg';

// iPad-first breakpoints
// iPad portrait: ~768-834px, iPad landscape: ~1024-1194px
// We define custom breakpoints for iPad-optimized experience
const BREAKPOINTS = {
  mobile: 600, // Below this is mobile
  ipadPortrait: 768, // iPad portrait starts here
  ipadLandscape: 1024, // iPad landscape starts here
  desktop: 1200, // Desktop starts here
} as const;

// Zod schema for form validation
const loungeAccessSchema = z.object({
  masseuse: z.number({ error: 'Please select a masseuse' }),
  locker: z
    .number({ error: 'Please select a locker' })
    .min(0, 'Please select a locker')
    .max(39, 'Locker number must be between 0 and 39'),
  customer: z
    .string({ error: 'Customer name is required' })
    .min(1, 'Customer name is required'),
});

type LoungeAccessFormData = z.infer<typeof loungeAccessSchema>;

// API request body type (matches what the API expects)
interface LoungeAccessRequestBody {
  masseuse: number;
  locker: number;
  customer: string;
}

// API response type
interface LoungeAccessSuccessResponse {
  token: string;
}

interface LoungeAccessErrorResponse {
  masseuse?: string[];
  locker?: string[];
  customer?: string[];
}

// Mock API call function for testing (returns mock token after delay)
const submitLoungeAccess = async (
  _data: LoungeAccessRequestBody,
): Promise<LoungeAccessSuccessResponse> => {
  // Simulate network delay
  // await new Promise((resolve) => setTimeout(resolve, 1000));

  // Return mock successful response
  // return {
  //   token:
  //     'eyJzZXNzaW9uX2lkIjoxNTQ0OTh9:1vhXXw:3keHDwcUXeNBqPa4ZsvNSAP8H_GeOan8_Zjjx3MFJ7w',
  // };

  // TODO: Uncomment below for real API call
  return customFetch<LoungeAccessSuccessResponse>('/answers/lounge_access/', {
    method: 'POST',
    body: _data,
  });
};

const LoungeAccessScreen = () => {
  const navigate = useNavigate();

  // iPad-first responsive breakpoints
  const isMobile = useMediaQuery(`(max-width:${BREAKPOINTS.mobile}px)`);
  const isIpadPortrait = useMediaQuery(
    `(min-width:${BREAKPOINTS.ipadPortrait}px) and (max-width:${BREAKPOINTS.ipadLandscape - 1}px)`,
  );
  const isIpadLandscape = useMediaQuery(
    `(min-width:${BREAKPOINTS.ipadLandscape}px) and (max-width:${BREAKPOINTS.desktop - 1}px)`,
  );
  const isIpad = isIpadPortrait || isIpadLandscape;

  const {
    control,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<LoungeAccessFormData>({
    resolver: zodResolver(loungeAccessSchema),
    defaultValues: {
      masseuse: undefined,
      locker: undefined,
      customer: '',
    },
  });

  // Fetch available masseuses
  const { masseuses, isLoading: isLoadingMasseuses } = useAvailableMasseuses();

  // Mutation for submitting lounge access
  const mutation = useMutation({
    mutationFn: submitLoungeAccess,
    onSuccess: (data, variables) => {
      // Store the token and expiration time (15 minutes from now)
      const expirationTime = Date.now() + 15 * 60 * 1000; // 15 minutes
      localStorage.setItem('loungeAccessToken', data.token);
      localStorage.setItem(
        'loungeAccessTokenExpiry',
        expirationTime.toString(),
      );
      // Get the selected masseuse's name for the URL
      const selectedMasseuse = masseuses.find(
        (m) => m.id === variables.masseuse,
      );
      // Encode params to hide from plain view
      const paramsData = {
        masseuseId: String(variables.masseuse),
        masseuseName: selectedMasseuse?.name ?? '',
        customerName: variables.customer,
      };
      const encodedParams = btoa(JSON.stringify(paramsData));
      // Navigate to the lounge survey page with encoded info
      navigate(`/lounge-survey?data=${encodedParams}`);
    },
    onError: (error: ApiError) => {
      // Set form errors from API response
      const errorBody = error.body as LoungeAccessErrorResponse | undefined;
      if (errorBody?.masseuse) {
        setError('masseuse', { message: errorBody.masseuse[0] });
      }
      if (errorBody?.locker) {
        setError('locker', { message: errorBody.locker[0] });
      }
      if (errorBody?.customer) {
        setError('customer', { message: errorBody.customer[0] });
      }
      // Note: __all__ errors are handled in the Alert component below
    },
  });

  const onSubmit = (data: LoungeAccessFormData) => {
    mutation.mutate(data);
  };

  // Responsive sizing for iPad-first design
  const logoWidth = isMobile ? 200 : isIpad ? 280 : 250;
  const cardPadding = isMobile ? 24 : isIpad ? 48 : 30;
  const formSpacing = isMobile ? 2 : isIpad ? 4 : 2;
  const inputSize = isMobile ? 'small' : 'medium'; // Larger inputs on iPad for better touch
  const avatarSize = isMobile ? 32 : isIpad ? 64 : 36; // Much larger avatars on iPad for visibility

  return (
    <Box
      sx={{
        background: `url(${LOUNGE_BG}) center center no-repeat`,
        backgroundSize: 'cover',
        boxShadow: 'inset 0 0 0 2000px rgba(0, 0, 0, 0.3)',
        // Full viewport height with no scrolling - app-like experience
        height: ['100vh', '100dvh'],
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Card
          sx={{
            // iPad-first card sizing
            width: '100%',
            maxWidth: {
              xs: '100%', // Mobile: full width with container padding
              sm: 520, // iPad portrait: wider for locker grid
              md: 560, // iPad landscape: slightly wider
              lg: 520, // Desktop: comfortable width
            },
            background: 'rgba(255,255,255,0.97)',
            borderRadius: { xs: 2, sm: 3 },
            // Subtle shadow for depth
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          }}
          variant="outlined"
        >
          <CardContent
            sx={{
              textAlign: 'center',
              p: `${cardPadding}px !important`,
            }}
          >
            <img
              src={CD_LOGO}
              width={logoWidth}
              alt="Club Dynasty Logo"
              style={{ maxWidth: '100%', height: 'auto' }}
            />
            <Typography
              variant={isIpad ? 'h4' : 'h5'}
              gutterBottom
              sx={{
                mt: { xs: 1, sm: 1.5 },
                fontWeight: 500,
              }}
            >
              Please rate your experience
            </Typography>
            <Box sx={{ mb: { xs: 3, sm: 4 } }}>
              <Typography
                variant={isIpad ? 'body1' : 'body2'}
                color="text.secondary"
              >
                Please fill in the details below to continue
              </Typography>
            </Box>

            {mutation.isError &&
              !errors.masseuse &&
              !errors.locker &&
              !errors.customer && (
                <Alert
                  severity="error"
                  sx={{
                    mb: { xs: 2, sm: 3 },
                    fontSize: isIpad ? '1rem' : undefined,
                  }}
                >
                  {getLoungeAccessErrorMessage(
                    (mutation.error as ApiError)?.body as ApiErrorBody,
                  )}
                </Alert>
              )}

            <form onSubmit={handleSubmit(onSubmit)}>
              <Controller
                name="masseuse"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <FormControl
                    fullWidth
                    error={!!errors.masseuse}
                    size={inputSize}
                    sx={{ mb: formSpacing }}
                  >
                    <InputLabel
                      id="masseuse-select-label"
                      sx={{
                        fontSize: { xs: '1rem', sm: '1.25rem' },
                      }}
                    >
                      Select Masseuse
                    </InputLabel>
                    <Select
                      labelId="masseuse-select-label"
                      id="masseuse-select"
                      value={value ?? ''}
                      label="Select Masseuse"
                      onChange={(e) => {
                        const val = e.target.value as string | number;
                        onChange(val === '' ? undefined : Number(val));
                      }}
                      endAdornment={
                        isLoadingMasseuses ? (
                          <CircularProgress
                            color="inherit"
                            size={isIpad ? 28 : 20}
                            sx={{ mr: 3 }}
                          />
                        ) : null
                      }
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxHeight: { xs: 300, sm: 400, md: 450 },
                          },
                        },
                      }}
                      sx={{
                        '& .MuiInputBase-input': {
                          fontSize: { xs: '1rem', sm: '1.25rem' },
                          py: { xs: 1.5, sm: 2 },
                          minHeight: { xs: 'auto', sm: 40 },
                          display: 'flex',
                          alignItems: 'center',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderWidth: { xs: 1, sm: 2 },
                        },
                        '& .MuiOutlinedInput-notchedOutline legend': {
                          fontSize: { xs: '0.75rem', sm: '0.9375rem' },
                        },
                      }}
                      renderValue={(selectedValue) => {
                        const selectedMasseuse = masseuses.find(
                          (m) => m.id === selectedValue,
                        );
                        if (!selectedMasseuse) return '';
                        const avatarSrc =
                          selectedMasseuse.image ||
                          (selectedMasseuse.technique === 'Shiatsu'
                            ? SHIATSU_FALLBACK_IMAGE
                            : undefined);
                        return (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: { xs: 1.5, sm: 2 },
                            }}
                          >
                            <Avatar
                              src={avatarSrc}
                              alt={selectedMasseuse.name}
                              sx={{
                                width: avatarSize,
                                height: avatarSize,
                                flexShrink: 0,
                              }}
                            />
                            <Typography
                              sx={{
                                fontSize: { xs: '0.95rem', sm: '1.25rem' },
                                fontWeight: 500,
                              }}
                            >
                              {selectedMasseuse.name}
                            </Typography>
                          </Box>
                        );
                      }}
                    >
                      {masseuses.map((option) => {
                        const avatarSrc =
                          option.image ||
                          (option.technique === 'Shiatsu'
                            ? SHIATSU_FALLBACK_IMAGE
                            : undefined);
                        return (
                          <MenuItem
                            key={option.id}
                            value={option.id}
                            sx={{
                              minHeight: { xs: 48, sm: 80 },
                              py: { xs: 1, sm: 2 },
                              px: { xs: 2, sm: 3 },
                              borderBottom: '1px solid',
                              borderColor: 'grey.200',
                              '&:last-child': {
                                borderBottom: 'none',
                              },
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: 'auto',
                                mr: { xs: 1.5, sm: 3 },
                              }}
                            >
                              <Avatar
                                src={avatarSrc}
                                alt={option.name}
                                sx={{
                                  width: avatarSize,
                                  height: avatarSize,
                                }}
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={option.name}
                              primaryTypographyProps={{
                                sx: {
                                  fontSize: { xs: '0.95rem', sm: '1.25rem' },
                                  fontWeight: 500,
                                },
                              }}
                            />
                          </MenuItem>
                        );
                      })}
                    </Select>
                    <FormHelperText>
                      {errors.masseuse?.message ||
                        'Select the masseuse who provided your session today'}
                    </FormHelperText>
                  </FormControl>
                )}
              />

              {/* Customer Name */}
              <Controller
                name="customer"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Your Name"
                    variant="outlined"
                    size={inputSize}
                    error={!!errors.customer}
                    helperText={
                      errors.customer?.message ||
                      'Your first name or nickname is fine'
                    }
                    fullWidth
                    slotProps={{
                      htmlInput: {
                        // Proper keyboard with autocapitalize
                        autoCapitalize: 'words',
                        // Prevent zoom on iOS when focusing input
                        style: { fontSize: isIpad ? '18px' : '16px' },
                      },
                    }}
                    sx={{
                      mb: formSpacing,
                      '& .MuiInputBase-root': {
                        fontSize: { xs: '1rem', sm: '1.25rem' },
                        minHeight: { xs: 'auto', sm: 64 },
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: { xs: '1rem', sm: '1.25rem' },
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderWidth: { xs: 1, sm: 2 },
                      },
                    }}
                  />
                )}
              />

              {/* Locker Number */}
              <Controller
                name="locker"
                control={control}
                render={({ field: { onChange, value, ...field } }) => (
                  <TextField
                    {...field}
                    label="Locker Number"
                    variant="outlined"
                    size={inputSize}
                    type="number"
                    value={value ?? ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      onChange(val === '' ? undefined : parseInt(val, 10));
                    }}
                    error={!!errors.locker}
                    helperText={
                      errors.locker?.message ||
                      'Enter the locker number assigned to you (0-39)'
                    }
                    fullWidth
                    slotProps={{
                      htmlInput: {
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                        min: 0,
                        max: 39,
                        style: { fontSize: isIpad ? '18px' : '16px' },
                      },
                    }}
                    sx={{
                      mb: { xs: 3, sm: 5 },
                      '& .MuiInputBase-root': {
                        fontSize: { xs: '1rem', sm: '1.25rem' },
                        minHeight: { xs: 'auto', sm: 64 },
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: { xs: '1rem', sm: '1.25rem' },
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderWidth: { xs: 1, sm: 2 },
                      },
                    }}
                  />
                )}
              />

              <Button
                variant="contained"
                disableElevation
                size="large"
                fullWidth
                type="submit"
                disabled={mutation.isPending}
                sx={{
                  // iPad-optimized touch target (minimum 44px, we go larger)
                  py: { xs: 1.5, sm: 2 },
                  fontSize: { xs: '1rem', sm: '1.15rem' },
                  fontWeight: 600,
                  borderRadius: { xs: 1, sm: 1.5 },
                  // Minimum touch target height
                  minHeight: { xs: 48, sm: 56 },
                }}
              >
                {mutation.isPending ? (
                  <>
                    Submitting...
                    <CircularProgress
                      color="inherit"
                      size={isIpad ? 24 : 20}
                      thickness={5}
                      sx={{ ml: 1 }}
                    />
                  </>
                ) : (
                  'Continue'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default LoungeAccessScreen;
